/**
 * PWAInstallPrompt — floating bottom pill encouraging install.
 * Uses the shared usePWAInstall hook so the rest of the app (user menu,
 * login screen) can share state.
 */
import React, { useState } from 'react';
import { X, Download, Share2 } from 'lucide-react';
import usePWAInstall from '../hooks/usePWAInstall';

const DISMISS_KEY = 'pwa_install_dismissed_v1';

export default function PWAInstallPrompt() {
  const { canInstall, isIos, isStandalone, install } = usePWAInstall();
  const [dismissed, setDismissed] = useState(
    typeof window !== 'undefined' && localStorage.getItem(DISMISS_KEY) === '1'
  );

  if (isStandalone || dismissed) return null;
  if (!canInstall && !isIos) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  const handleInstall = async () => {
    const choice = await install();
    if (choice?.outcome === 'accepted' || choice?.outcome === 'dismissed') {
      dismiss();
    }
  };

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] max-w-sm w-[90%] bg-white border border-slate-200 shadow-lg rounded-xl p-3 flex items-center gap-3"
      data-testid="pwa-install-prompt"
    >
      <div className="w-10 h-10 rounded-lg bg-indigo-600 text-white flex items-center justify-center flex-shrink-0 font-bold text-sm">
        GS
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-slate-900 truncate">
          Install CRM Sync
        </div>
        {isIos && !canInstall ? (
          <div className="text-xs text-slate-500 flex items-center gap-1">
            Tap <Share2 className="w-3 h-3 inline" /> Share ▸ "Add to Home Screen"
          </div>
        ) : (
          <div className="text-xs text-slate-500 truncate">
            Add to home screen for a native-app feel.
          </div>
        )}
      </div>
      {canInstall && (
        <button
          type="button"
          onClick={handleInstall}
          className="text-xs font-medium bg-indigo-600 hover:bg-indigo-700 text-white rounded-md px-3 py-1.5 flex items-center gap-1"
          data-testid="pwa-install-btn"
        >
          <Download className="w-3.5 h-3.5" />
          Install
        </button>
      )}
      <button
        type="button"
        onClick={dismiss}
        className="p-1 text-slate-400 hover:text-slate-600"
        data-testid="pwa-install-dismiss"
        aria-label="Dismiss install prompt"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
